import { ref, computed, watch, nextTick } from 'vue'
import log from 'loglevel'
import store from '@/store'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { PLACE_RELATIONSHIP } from '@/composables/member/UseUserStatusFilterCondition'
import { RestApiClient } from 'ifield-core-components'
import GeometryUtil from '@/js/util/GeometryUtil'

/**
 * 地図タブ上の地図での地物fit処理
 * @param {ShallowRef.<VueComponent>} map AppMapControl のインスタンス
 * @param {ShallowRef.<Object[]>} indoorPlaces 屋内プレイスマスタデータ
 * @param {Ref.<Object>} condition 絞り込み条件
 * @param {Ref.<Object[]>} users 現在のユーザーデータ
 */
export function useFitFeaturesOnMemberMap({
  map,
  indoorPlaces,
  condition,
  users,
}) {
  const { handleApiError } = useHandleApiError()

  /**
   * 対象ID
   * @type {Ref.<Object>}
   */
  const targetId = ref({
    indoorSite: null,
    indoorBuilding: null,
    indoorLevel: null,
    indoorPlace: null,
  })
  /**
   * 当モジュールの機能を有効化する場合はtrue
   * @type {Ref.<boolean>}
   */
  const enable = ref(true)

  /**
   * 現在のズーム値
   * @type {ComputedRef.<Number>}
   */
  const zoom = computed(() => store.getters['map/zoom'])
  /**
   * 屋外の場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const isGlobalMode = computed(() => store.getters['mapIndoor/isGlobalMode'])
  /**
   * サイトデータのMap
   * @type {ComputedRef.<Map>}
   */
  const indoorSiteMap = computed(
    () => store.getters['indoorSite/indoorSiteMap']
  )
  /**
   * ビルデータのMap
   * @type {ComputedRef.<Map>}
   */
  const indoorBuildingMap = computed(
    () => store.getters['indoorBuilding/indoorBuildingMap']
  )

  /**
   * 屋内プレイスのMap
   * @type {ComputedRef.<Map>}
   */
  const indoorPlaceMap = computed(
    () => new Map(indoorPlaces.value.map((p) => [p.id, p]))
  )
  /**
   * ユーザーのbounds
   * @type {ComputedRef.<Object>}
   */
  const usersBounds = computed(() => {
    if (!users.value.length) return null
    return GeometryUtil.getBounds(
      users.value.map((m) => m.geom.geometry)
    ).toObject()
  })
  /**
   * fitBounds時に指定するズームレベル
   * @type {ComputedRef.<Number>}
   */
  const zoomOnFitBounds = computed(() => {
    // 屋内時はデフォルトで20まで寄る
    const defaultZoom = isGlobalMode.value ? 18 : 20
    return Math.max(defaultZoom, zoom.value)
  })

  /**
   * 指定boundsに地図をfit
   * @param {Object} bounds 表示範囲
   */
  const fitToBounds = (bounds) => {
    log.debug('[DEBUG] fitToBounds', bounds, enable.value)
    if (map.value && enable.value) {
      map.value.fitBounds(bounds, zoomOnFitBounds.value)
    }
  }

  /**
   * 指定ジオメトリに地図をfitする
   * @param {Object} geometry ジオメトリ
   * @param {Number} bufferRadius バッファ半径(m) 指定時はこの数値分のバッファを含む範囲にfitする
   */
  const fitToGeometry = (geometry, bufferRadius) => {
    const geometryToFit = bufferRadius
      ? GeometryUtil.buffer(geometry, bufferRadius)
      : geometry
    const bounds = GeometryUtil.getBounds(geometryToFit).toObject()
    fitToBounds(bounds)
  }

  /**
   * 対象ユーザーの範囲に地図をfitする
   * @returns {Promise} fit実行後に解決するPromise
   */
  const fitToUsers = () => {
    if (map.value && usersBounds.value) {
      return new Promise((resolve) => {
        // 屋内アイテムへのfitを先に実行するためこちらにもnextTickを噛ませる
        nextTick()
          .then(() => fitToBounds(usersBounds.value))
          .then(() => resolve())
      })
    } else if (usersBounds.value) {
      // マップの初期化がまだの場合は待つ
      log.debug('[DEBUG] map is not initialized yet. watch map once')
      return new Promise((resolve) => {
        const unwatch = watch(map, () => {
          nextTick().then(() => fitToBounds(usersBounds.value))
          unwatch()
          resolve()
        })
      })
    }
  }

  /**
   * 選択された屋内アイテムに地図をfitする
   */
  const fitToIndoorItem = async () => {
    const {
      indoorSite: indoorSiteId,
      indoorBuilding: indoorBuildingId,
      indoorPlace: indoorPlaceId,
    } = targetId.value

    let bounds
    if (indoorPlaceId) {
      const place = indoorPlaceMap.value.get(indoorPlaceId)
      if (place && place.geom.geometry) {
        bounds = GeometryUtil.getBounds(place.geom.geometry).toObject()
      }
    } else if (indoorBuildingId) {
      const building = indoorBuildingMap.value.get(indoorBuildingId)
      if (building && building.bounds_geom) {
        bounds = GeometryUtil.getBounds(building.bounds_geom).toObject()
      }
    } else if (indoorSiteId) {
      const site = indoorSiteMap.value.get(indoorSiteId)
      if (site && site.bounds_geom) {
        bounds = GeometryUtil.getBounds(site.bounds_geom).toObject()
      }
    }

    if (bounds) {
      // 権限の無い建物の選択時に権限チェックを先に回すためnextTickを噛ませる
      await nextTick()
      fitToBounds(bounds)
    }
  }

  /**
   * プレイスへの地図fit処理
   * @param {Object} newCondition 新しい condition.place
   * @param {Object} oldCondition 古い conditioin.place
   */
  const fitByPlaceCondition = async (newCondition, oldCondition) => {
    const {
      placeId: newPlaceId,
      relationship: newRelationship,
      distance: newDistance,
    } = newCondition
    const {
      placeId: oldPlaceId,
      relationship: oldRelationship,
      distance: oldDistance,
    } = oldCondition

    if (!newPlaceId) return

    if (newPlaceId !== oldPlaceId) {
      // プレイス選択が変更された場合はそのプレイスにfit
      const radius =
        newRelationship === PLACE_RELATIONSHIP.DISTANCE ? newDistance : null
      await fitToPlaceById(newPlaceId, radius)
    } else if (newRelationship !== oldRelationship) {
      // 位置関係性変更時
      if (newRelationship === PLACE_RELATIONSHIP.DISTANCE) {
        await fitToPlaceById(newPlaceId, newDistance)
      } else if (oldRelationship === PLACE_RELATIONSHIP.DISTANCE) {
        await fitToPlaceById(newPlaceId)
      }
      // いる と チェックイン中 の間の変更は何もしない
    } else if (
      newRelationship === PLACE_RELATIONSHIP.DISTANCE &&
      newDistance !== oldDistance
    ) {
      // 距離指定で指定距離だけ変わった場合も新しい距離でfit
      await fitToPlaceById(newPlaceId, newDistance)
    }
  }

  /**
   * 指定IDのプレイスに地図をfitする
   * @param {Number} id プレイスID
   * @param {Number} radius fit時のバッファ半径(m)
   */
  const fitToPlaceById = async (id, radius) => {
    let geometry
    if (indoorPlaceMap.value.has(id)) {
      // 屋内プレイスデータに存在する場合はfetch不要
      geometry = indoorPlaceMap.value.get(id).geom?.geometry
    } else {
      const place = await getSpecificPlace(id)
      geometry = place?.geom?.geometry
    }

    if (geometry) {
      // 屋内アイテムへのfitを先に実行するためこちらにもnextTickを噛ませる
      await nextTick()
      fitToGeometry(geometry, radius)
    }
  }

  /**
   * 特定のプレイスを取得する
   * @param {Number} id プレイスID
   * @returns {Object} プレイスデータ
   */
  const getSpecificPlace = async (id) => {
    const url = RestApiClient.createApiUrl(`place/${id}`)
    try {
      const json = await RestApiClient.executeGetApi(url)
      return json.data
    } catch (error) {
      handleApiError(error, () => {
        log.error('Failed to fetch place', id)
      })
    }
  }

  watch(
    // 比較が中身も含めて単なる文字列比較で済むので文字列化して監視する
    [
      () => JSON.stringify(targetId.value),
      () => JSON.stringify(condition.value.place),
    ],
    ([newTargetId, newPlaceCondition], [oldTargetId, oldPlaceCondition]) => {
      if (newTargetId !== oldTargetId) {
        fitToIndoorItem()
      }
      if (newPlaceCondition !== oldPlaceCondition) {
        fitByPlaceCondition(
          newPlaceCondition ? JSON.parse(newPlaceCondition) : {},
          oldPlaceCondition ? JSON.parse(oldPlaceCondition) : {}
        )
      }
    },
    { immediate: true }
  )

  return {
    targetId,
    enable,
    fitToBounds,
    fitToGeometry,
    fitToUsers,
  }
}
